/**
 * "What do you feel like playing?" - the fastest way in for someone who does
 * not know what they want yet.
 *
 * The eight moods are a real filter, not decoration: choosing one filters the
 * pick below it. Two of them have nothing behind them today and are drawn as
 * open, dashed tokens with a "coming soon" stamp, so the taxonomy stays
 * honest instead of padded.
 */

import { MOODS, moodStatus } from "@kikik/game-catalog";
import type { MoodId } from "@kikik/game-catalog";
import { useI18n } from "../../i18n";
import { Motif } from "../art/Motif";

export interface IntentSectionProps {
  active: MoodId | null;
  onSelect: (mood: MoodId | null) => void;
}

export function IntentSection({ active, onSelect }: IntentSectionProps) {
  const { t } = useI18n();

  return (
    <section className="section section--intent" id="intent" aria-labelledby="intent-title" data-reveal="out">
      <div className="shell">
        <p className="eyebrow">{t("portal.intent.eyebrow")}</p>
        <h2 className="section__title" id="intent-title">
          {t("portal.intent.title")}
        </h2>
        <p className="section__lead">{t("portal.intent.lead")}</p>

        <ul className="moods">
          {MOODS.map((mood) => {
            const open = moodStatus(mood.id) === "open";
            const selected = active === mood.id;
            return (
              <li key={mood.id} className="moods__cell" data-accent={mood.accent} data-open={open}>
                <button
                  type="button"
                  className="mood"
                  data-mood={mood.id}
                  aria-pressed={selected}
                  onClick={() => onSelect(selected ? null : mood.id)}
                >
                  <Motif motif={mood.motif} className="mood__art" />
                  <span className="mood__name">{t(mood.nameKey)}</span>
                  <span className="mood__meta">
                    {open ? t("portal.badge.oneGame") : t("portal.status.comingSoon")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {active ? (
          <p className="moods__status" role="status">
            {t("portal.intent.showing", { mood: t(MOODS.find((m) => m.id === active)!.nameKey) })}{" "}
            <button type="button" className="linkbtn" onClick={() => onSelect(null)}>
              {t("portal.intent.clear")}
            </button>
          </p>
        ) : null}
      </div>
    </section>
  );
}
